/**
 * Store drawer — sliding panel navigation for Shop and Courses, and the whole
 * menu at every breakpoint.
 *
 * Three things about the environment shape this file:
 *
 * 1. Everything is delegated from `document`. The header is re-rendered
 *    through the Section Rendering API, so anything bound directly to a node
 *    inside it stops working the moment that section is replaced.
 * 2. The drawer is moved to <body> on first use. It is authored inside the
 *    header nav, and any transformed ancestor there would capture a
 *    position: fixed descendant and clip the drawer to the header.
 * 3. No requestAnimationFrame. rAF is throttled to nothing in a background or
 *    hidden tab, and a menu that only opens when the tab is focused is a menu
 *    that is sometimes simply broken. Every state flip here forces a synchronous
 *    reflow instead, which is what actually makes a CSS transition start.
 *
 * TWO CONTEXTS. `data-context` is either `shop` or `training`, set by the two
 * pills above the search box and by the header's own triggers. The context
 * decides which icon rank is visible, which root panel is in the track, the
 * search placeholder, the wording on the gift voucher chip, and WHICH HALF OF
 * THE MENU SEARCH LOOKS IN.
 *
 * Both ranks use the same `.sd-switch__btn` class and are scoped by their
 * containing `.sd-switch`, so selecting a store never deselects a stage.
 *
 * Panels are all server-rendered and parked in `.sd-panels`. Opening one moves
 * it into the track; going back moves it home again.
 *
 * FETCHED ON DEMAND (2026-09-15). The whole taxonomy — 45 panels, 311 rows —
 * used to be rendered into every page of the site, 21.7 KB compressed, for a
 * menu that is shut until someone clicks it. It now comes from `/?view=sd`
 * (templates/index.sd.liquid) the first time it is needed, and is warmed on
 * the first hover or touch of any trigger, so the click itself almost never
 * waits. See `warm()` below.
 *
 * If that fetch fails the component stands down: it sets
 * `window.__storeDrawerFailed`, which store-drawer-mobile.js reads, and hands
 * the click back to the theme's own MenuDrawer — which renders the same menu
 * and ships on every page anyway. A broken network must not cost the visitor
 * their navigation.
 */
(function () {
  'use strict';
  if (window.__storeDrawer) return;
  window.__storeDrawer = true;

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  var drawer = null, track = null, store = null;
  var stack = [], opener = null, padWas = '';
  var INDEX = null;

  var HINT_SHOP = 'Search a product, brand or SKU';
  var HINT_COURSE = 'Search a course or speciality';
  /* These must match the section headings in the drawer. */
  var WORD = { shop: 'Gear', training: 'Courses' };

  /* Abbreviations divers actually type. An alias is an editorial decision —
     "bcd means Buoyancy Control Devices" — so it OUTRANKS an accidental prefix
     match, otherwise a parent keeps losing to its own accessory children,
     which is the exact bug this exists to fix. */
  var ALIAS = {
    bcd: 'buoyancy control devices', bc: 'buoyancy control devices',
    spg: 'pressure gauges', octo: 'octopus', smb: 'surface markers',
    dsmb: 'surface markers', reg: 'regulator', comp: 'dive computer',
    viz: 'mask', wing: 'wing', drysuit: 'drysuit', semi: 'semi-dry',
    torch: 'torch', nitrox: 'nitrox', ow: 'open water', efr: 'first aid',
    dsd: 'try scuba', dm: 'divemaster', aow: 'advanced open water',
    kit: 'builder', set: 'builder', rig: 'hard gear builder',
    /* Marine & Sailing and Watersports (2026-10-10). The collections say
       "Lifejackets" as one word; people type two. */
    'life jacket': 'lifejacket', 'life vest': 'lifejacket', lifevest: 'lifejacket',
    pfd: 'buoyancy aids', sup: 'paddleboard', 'stand up paddle': 'paddleboard'
  };

  function flush(el) { return el && el.offsetHeight; }
  function currentContext() {
    return drawer && drawer.getAttribute('data-context') === 'training' ? 'training' : 'shop';
  }

  function adopt() {
    var found = document.querySelectorAll('[data-store-drawer]');
    if (!found.length) return null;
    for (var i = 1; i < found.length; i++) found[i].remove();
    var el = found[0];
    if (el.parentNode !== document.body) document.body.appendChild(el);
    return el;
  }

  /* ---- fetching the drawer ------------------------------------------------
     `warm()` is safe to call as often as you like: it returns the in-flight
     promise if there is one, and resolves true the moment the markup is in the
     document. The response is the drawer and nothing else — index.sd.liquid
     uses `layout none` — so it is parsed in a detached div and only the
     [data-store-drawer] element is adopted, never the whole fragment. */
  var SD_URL = '/?view=sd';
  var pendingFetch = null;

  /* Shopify sends `cache-control: private, no-store` on storefront responses,
     so the browser re-fetches the drawer on every page even for someone who
     opens the menu constantly. sessionStorage carries it across navigations
     instead. The key is the deployed asset version of this very file, so a
     theme deploy invalidates it without anyone remembering to. Every access is
     wrapped: Safari private mode throws on setItem, and a quota failure must
     cost the visitor nothing more than a re-fetch. */
  // Cache key revision: bump when the drawer's markup changes without a change to this file,
  // so an open tab cannot keep serving drawer HTML the new CSS no longer styles. 2026-09-24:
  // the "Popular right now" chips were removed and the Technical / Brand row moved into the list.
  var SD_REV = '2026-09-27';
  var SD_KEY = (function () {
    var el = document.querySelector('script[src*="store-drawer.js"]');
    var v = el && el.src && el.src.split('v=')[1];
    return 'sd:' + (v || '0') + ':' + SD_REV;
  })();

  function fromSession() {
    try { return sessionStorage.getItem(SD_KEY); } catch (e) { return null; }
  }
  function toSession(html) {
    try {
      /* Drop any older version's copy rather than letting them accumulate. */
      for (var i = sessionStorage.length - 1; i >= 0; i--) {
        var k = sessionStorage.key(i);
        if (k && k.indexOf('sd:') === 0 && k !== SD_KEY) sessionStorage.removeItem(k);
      }
      sessionStorage.setItem(SD_KEY, html);
    } catch (e) {}
  }

  function injectFrom(html) {
    if (document.querySelector('[data-store-drawer]')) return true;
    var tmp = document.createElement('div');
    tmp.innerHTML = html;
    var el = tmp.querySelector('[data-store-drawer]');
    if (!el) return false;
    document.body.appendChild(el);
    drawer = null; INDEX = null;
    return true;
  }

  function warm() {
    if (document.querySelector('[data-store-drawer]')) return Promise.resolve(true);
    if (window.__storeDrawerFailed) return Promise.resolve(false);
    if (pendingFetch) return pendingFetch;

    var cached = fromSession();
    if (cached && injectFrom(cached)) return Promise.resolve(true);

    pendingFetch = fetch(SD_URL, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var ok = injectFrom(html);
        if (ok) toSession(html);
        else window.__storeDrawerFailed = true;
        return ok;
      })
      .catch(function () {
        /* One failure is enough. Retrying on every click would leave the menu
           feeling dead while the requests pile up. */
        window.__storeDrawerFailed = true;
        pendingFetch = null;
        return false;
      });
    return pendingFetch;
  }

  /* Hand the visitor back to the theme's MenuDrawer, which carries the same
     menu. __storeDrawerFailed is already set, so store-drawer-mobile.js will
     not intercept this click the way it intercepted the last one. */
  function standDown() {
    var btn = document.querySelector('.menu-drawer-button, [aria-controls="MenuDrawer"]');
    if (btn) btn.click();
  }

  function init() {
    if (drawer && drawer.isConnected) return drawer;
    drawer = adopt();
    if (!drawer) return null;
    track = drawer.querySelector('[data-sd-track]');
    store = drawer.querySelector('[data-sd-panels]');
    INDEX = null;
    return drawer;
  }

  function panelById(id) {
    return drawer.querySelector('[data-sd-panel="' + CSS.escape(id) + '"]');
  }
  function tileFor(id) {
    return drawer.querySelector('.sd-switch__btn[data-sd-go="' + CSS.escape(id) + '"]');
  }

  /* ---- search index -------------------------------------------------------
     Built from the panels already in the page rather than from a second copy
     of the taxonomy, so it can never drift and costs nothing to ship.

     Every entry carries the context its panel belongs to. Course panels are
     `sd-t*`; everything else, brands and the gear builders included, is gear.
     Searching from the Courses side must never surface a wetsuit, and
     searching from Shop must never surface a course (Devon, 2026-09-10). */
  function ctxOfPanel(pid) {
    /* sd-t0, sd-t0-g2 — the digit matters. Without it `sd-tec` matches and
       the Technical Store is indexed as a course (2026-09-21). */
    return /^sd-t\d/.test(pid) ? 'training' : 'shop';
  }
  function crumbOf(panel) {
    var head = panel.querySelector('.sd-head__name');
    var back = panel.querySelector('.sd-back');
    if (head && back) return back.textContent.trim() + ' › ' + head.textContent.trim();
    if (head) return head.textContent.trim();
    /* A root panel takes its name from its own rank tile — "Scuba", "Learn" —
       which is shorter and truer than the "View …" row at the bottom of it. */
    var pid = panel.getAttribute('data-sd-panel') || '';
    var tile = tileFor(pid);
    var lab = tile && tile.querySelector('.sd-switch__label');
    if (lab) return lab.textContent.trim();
    var all = panel.querySelector('.sd-row--all .sd-row__link');
    return all ? all.textContent.replace(/^\s*View\s+/i, '').trim() : '';
  }
  function buildIndex() {
    if (INDEX) return INDEX;
    INDEX = [];
    var panels = drawer.querySelectorAll('[data-sd-panel]');
    Array.prototype.forEach.call(panels, function (p) {
      var pid = p.getAttribute('data-sd-panel');
      var crumb = crumbOf(p);
      var ctx = ctxOfPanel(pid);
      /* Technical, Marine & Sailing and Watersports are tiles in the store
         rank like Scuba (2026-10-10), so their rows are Gear like any store's. */
      var isStoreRoot = /^sd-s\d+$/.test(pid) || pid === 'sd-tec' || pid === 'sd-mar' || pid === 'sd-wat';
      var isCourseRoot = /^sd-t\d+$/.test(pid);
      var isBrands = pid === 'sd-brands';
      var isBuilders = pid === 'sd-builders';
      Array.prototype.forEach.call(p.querySelectorAll('.sd-row'), function (row) {
        if (row.classList.contains('sd-row--all')) return;
        var el = row.querySelector('.sd-row__link, .sd-row__btn');
        if (!el) return;
        /* A row with a descriptor has two lines of text; the first is the
           title and the only part worth matching or showing. */
        var titleEl = el.querySelector('.sd-row__stack > span') || el;
        var text = titleEl.textContent.replace(/\s+/g, ' ').trim();
        if (!text) return;
        var tag = '';
        if (isBrands) tag = 'Brand';
        else if (isBuilders) tag = 'Builder';
        else if (isStoreRoot) tag = 'Gear';
        else if (isCourseRoot) tag = 'Course';
        INDEX.push({
          t: text,
          low: text.toLowerCase(),
          ctx: ctx,
          crumb: isBrands ? 'All products by ' + text : crumb,
          tag: tag,
          href: el.tagName === 'A' ? el.getAttribute('href') : null,
          go: el.tagName === 'BUTTON' ? el.getAttribute('data-sd-go') : null
        });
      });
    });
    return INDEX;
  }
  function query(raw) {
    var s = raw.trim().toLowerCase();
    if (!s) return [];
    var ctx = currentContext();
    var extra = [];
    Object.keys(ALIAS).forEach(function (k) { if (k.indexOf(s) === 0 || s === k) extra.push(ALIAS[k]); });
    var terms = [s].concat(extra);
    var seen = {}, out = [];
    buildIndex().forEach(function (it) {
      if (it.ctx !== ctx) return;
      var rank = -1;
      terms.forEach(function (t, ti) {
        var i = it.low.indexOf(t);
        if (i === 0) rank = Math.max(rank, ti === 0 ? 3 : 4);
        else if (i > 0) rank = Math.max(rank, ti === 0 ? 1 : 2);
      });
      if (rank < 0) return;
      var key = it.t + '|' + it.crumb;
      if (seen[key]) return;
      seen[key] = 1;
      out.push({ it: it, rank: rank });
    });
    out.sort(function (a, b) {
      return b.rank - a.rank || a.it.t.length - b.it.t.length || a.it.t.localeCompare(b.it.t);
    });
    return out.slice(0, 40).map(function (x) { return x.it; });
  }
  function esc(s) { return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;'); }
  function highlight(name, raw) {
    var s = raw.trim();
    if (!s) return esc(name);
    var i = name.toLowerCase().indexOf(s.toLowerCase());
    if (i < 0) return esc(name);
    return esc(name.slice(0, i)) + '<mark>' + esc(name.slice(i, i + s.length)) + '</mark>' + esc(name.slice(i + s.length));
  }
  var ARROW = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>';

  function renderResults(raw) {
    var box = drawer.querySelector('[data-sd-found]');
    if (!box) return;
    var word = WORD[currentContext()];
    var searchUrl = (window.Shopify && Shopify.routes && Shopify.routes.search_url) || '/search';
    var hand = '<a class="sd-hand" href="' + searchUrl + '?q=' + encodeURIComponent(raw.trim()) + '">Search the whole store for “' + esc(raw.trim()) + '”' + ARROW + '</a>';
    var r = query(raw);
    if (!r.length) {
      box.innerHTML = '<p class="sd-empty">Nothing in <b>' + word + '</b> matches “' + esc(raw.trim()) + '”. Switch tab to look in ' +
        (currentContext() === 'training' ? 'Gear' : 'Courses') + ', or use the row below.</p>' + hand;
      return;
    }
    box.innerHTML =
      '<p class="sd-secthd">' + r.length + ' match' + (r.length === 1 ? '' : 'es') + ' in ' + word + '</p>' +
      '<ul class="sd-res">' + r.map(function (it) {
        var inner = '<span class="sd-res__t">' + highlight(it.t, raw) +
          (it.tag ? '<span class="sd-res__tag">' + it.tag + '</span>' : '') + '</span>' +
          (it.crumb ? '<span class="sd-res__c">' + esc(it.crumb) + '</span>' : '');
        return '<li>' + (it.href
          ? '<a href="' + it.href + '">' + inner + '</a>'
          : '<a href="#" data-sd-result="' + esc(it.go || '') + '">' + inner + '</a>') + '</li>';
      }).join('') + '</ul>' + hand;
  }
  function setSearching(on) { drawer.classList.toggle('is-searching', !!on); }
  function rerunSearch() {
    var i = drawer.querySelector('[data-sd-q]');
    if (i && i.value.trim() && drawer.classList.contains('is-searching')) renderResults(i.value);
  }

  /* ---- panels ------------------------------------------------------------ */
  function slide(instant) {
    if (!track) return;
    var jump = instant || REDUCED.matches;
    if (jump) track.classList.add('is-instant');
    track.style.transform = 'translateX(-' + (stack.length - 1) * 100 + '%)';
    drawer.setAttribute('data-depth', String(stack.length - 1));
    if (jump) { flush(track); track.classList.remove('is-instant'); }
  }
  function clearStack() {
    while (stack.length) store.appendChild(stack.pop());
    track.classList.add('is-instant');
    track.style.transform = 'translateX(0)';
    drawer.setAttribute('data-depth', '0');
    flush(track);
    track.classList.remove('is-instant');
  }
  function root(id, instant) {
    var p = panelById(id);
    if (!p) return;
    clearStack();
    track.appendChild(p);
    stack.push(p);
    p.scrollTop = 0;
    slide(instant !== false);
  }
  function push(id) {
    var p = panelById(id);
    if (!p || stack.indexOf(p) !== -1) return;
    track.appendChild(p);
    stack.push(p);
    p.scrollTop = 0;
    flush(p);
    slide(false);
    var back = p.querySelector('.sd-back');
    if (back) back.focus({ preventScroll: true });
  }
  function pop() {
    if (stack.length < 2) return;
    var leaving = stack.pop();
    slide(false);
    var landing = stack[stack.length - 1];
    var btn = landing && landing.querySelector('.sd-row__btn');
    if (btn) btn.focus({ preventScroll: true });
    window.setTimeout(function () { if (store) store.appendChild(leaving); }, 320);
  }

  /* Scoped to the rank that owns the button: the two ranks share a class, and
     deselecting across both would leave the hidden one with no selection. */
  function selectRank(id, btn) {
    var rank = btn && btn.closest('.sd-switch');
    if (rank) {
      Array.prototype.forEach.call(rank.querySelectorAll('.sd-switch__btn'), function (b) {
        b.setAttribute('aria-selected', String(b === btn));
      });
    }
    root(id, true);
  }

  function setMode(context, panelId) {
    drawer.setAttribute('data-context', context);
    Array.prototype.forEach.call(drawer.querySelectorAll('[data-sd-mode]'), function (b) {
      b.setAttribute('aria-selected', String(b.getAttribute('data-sd-mode') === context));
    });
    var input = drawer.querySelector('[data-sd-q]');
    if (input) input.placeholder = context === 'training' ? HINT_COURSE : HINT_SHOP;

    /* The voucher chip is pinned in the foot and never moves, but it reads
       differently depending on which half of the shop you are in — gifting a
       course is a different proposition from gifting kit. BOTH STRINGS LIVE IN
       LIQUID, on the element's own data attributes; this only picks one. */
    var gift = drawer.querySelector('[data-sd-gift]');
    if (gift) {
      var giftLabel = gift.querySelector('[data-sd-gift-label]');
      var wording = gift.getAttribute(context === 'training' ? 'data-label-training' : 'data-label-shop');
      if (giftLabel && wording) giftLabel.textContent = wording;
    }

    var rank = drawer.querySelector(context === 'training' ? '.sd-switch--course' : '.sd-switch--shop');
    var btn = null;
    if (rank) {
      if (panelId) btn = rank.querySelector('.sd-switch__btn[data-sd-go="' + CSS.escape(panelId) + '"]');
      if (!btn) btn = rank.querySelector('.sd-switch__btn[aria-selected="true"]') || rank.querySelector('.sd-switch__btn');
    }
    if (btn) selectRank(btn.getAttribute('data-sd-go'), btn);
    else if (panelId) root(panelId, true);

    /* Switching side with a live query re-runs it against the other half
       rather than leaving stale results on screen. */
    rerunSearch();
  }

  /* ---- page lock ----------------------------------------------------------
     `html.sd-open` is this drawer's public signal that a modal is up. It exists
     because the Shopify Inbox bubble carries `z-index: 2147483000` against this
     drawer's 100, so without it the chat button floats on top of the drawer's
     own scrim — visible and clickable outside the modal it is sitting on.
     site-interactions.css hides it on this class. The theme's own
     `body.has-modal-opening` was not reused: it could not be verified as
     removed on close, and a stuck class would hide chat forever.
     REMOVE THE CLASS IN unlock() OR CHAT NEVER COMES BACK. */
  function lock() {
    var gap = window.innerWidth - document.documentElement.clientWidth;
    padWas = document.documentElement.style.paddingInlineEnd;
    if (gap > 0) document.documentElement.style.paddingInlineEnd = gap + 'px';
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.classList.add('sd-open');
  }
  function unlock() {
    document.documentElement.style.overflow = '';
    document.documentElement.style.paddingInlineEnd = padWas;
    document.documentElement.classList.remove('sd-open');
  }

  function open(context, panelId, trigger) {
    if (init()) { openNow(context, panelId, trigger); return; }
    /* Not in the document yet. Nothing visible happens for as long as the
       fetch takes, which is why triggers are warmed on hover and touch. */
    warm().then(function (ok) {
      if (ok && init()) openNow(context, panelId, trigger);
      else standDown();
    });
  }

  function openNow(context, panelId, trigger) {
    opener = trigger || null;

    var input = drawer.querySelector('[data-sd-q]');
    if (input) input.value = '';
    setSearching(false);

    setMode(context === 'training' ? 'training' : 'shop', panelId);

    drawer.hidden = false;
    lock();
    flush(drawer);
    drawer.classList.add('is-open');
    var close = drawer.querySelector('[data-sd-close]');
    if (close && close.focus) close.focus({ preventScroll: true });
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
  }

  function close() {
    if (!drawer || drawer.hidden) return;
    drawer.classList.remove('is-open');
    document.querySelectorAll('[data-sd-open]').forEach(function (b) {
      b.setAttribute('aria-expanded', 'false');
    });
    window.setTimeout(function () {
      if (!drawer || drawer.classList.contains('is-open')) return;
      drawer.hidden = true;
      unlock();
    }, REDUCED.matches ? 0 : 320);
    if (opener && opener.isConnected) opener.focus({ preventScroll: true });
    opener = null;
  }

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!(t instanceof Element)) return;

    var trigger = t.closest('[data-sd-open]');
    if (trigger) {
      e.preventDefault();
      open(trigger.getAttribute('data-sd-open'), trigger.getAttribute('data-sd-panel-id'), trigger);
      return;
    }
    if (!drawer || drawer.hidden) return;

    if (t.closest('[data-sd-close]')) { e.preventDefault(); close(); return; }

    var pill = t.closest('[data-sd-mode]');
    if (pill) {
      e.preventDefault();
      setMode(pill.getAttribute('data-sd-mode'));
      return;
    }

    if (t.closest('[data-sd-clear]')) {
      e.preventDefault();
      var i = drawer.querySelector('[data-sd-q]');
      if (i) { i.value = ''; i.focus(); }
      setSearching(false);
      return;
    }
    var result = t.closest('[data-sd-result]');
    if (result) {
      var go = result.getAttribute('data-sd-result');
      if (go) {
        e.preventDefault();
        var i2 = drawer.querySelector('[data-sd-q]');
        if (i2) i2.value = '';
        setSearching(false);
        push(go);
      }
      return;
    }
    if (t.closest('.sd-back')) { e.preventDefault(); pop(); return; }

    var az = t.closest('[data-sd-letter]');
    if (az) {
      e.preventDefault();
      var target = drawer.querySelector('#sd-L-' + CSS.escape(az.getAttribute('data-sd-letter')));
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    var rankBtn = t.closest('.sd-switch__btn');
    if (rankBtn) { e.preventDefault(); selectRank(rankBtn.getAttribute('data-sd-go'), rankBtn); return; }

    var more = t.closest('.sd-row__btn[data-sd-go], .sd-index button[data-sd-go]');
    if (more) { e.preventDefault(); push(more.getAttribute('data-sd-go')); return; }

    /* The A–Z button in the search box (2026-10-10). It pushes the brand list
       over whichever store is open, so Back returns to that store. */
    var pushBtn = t.closest('[data-sd-push]');
    if (pushBtn) { e.preventDefault(); push(pushBtn.getAttribute('data-sd-push')); return; }

    /* Fallback close. The scrim is an empty div and theme.css has hidden it
       before now; without this the whole page becomes unclickable. */
    if (t.closest('[data-store-drawer]') && !t.closest('.store-drawer__panel')) close();
  });

  document.addEventListener('input', function (e) {
    if (!drawer || drawer.hidden) return;
    var i = e.target;
    if (!(i instanceof Element) || !i.hasAttribute('data-sd-q')) return;
    var v = i.value || '';
    if (v.trim()) { setSearching(true); renderResults(v); }
    else setSearching(false);
  });

  document.addEventListener('keydown', function (e) {
    if (!drawer || drawer.hidden) return;
    if (e.key === 'Escape') {
      var i = drawer.querySelector('[data-sd-q]');
      if (i && i.value) { i.value = ''; setSearching(false); i.focus(); return; }
      e.preventDefault(); close(); return;
    }
    if (e.key !== 'Tab') return;
    var panel = drawer.querySelector('.store-drawer__panel');
    var live = Array.prototype.filter.call(
      panel.querySelectorAll('a[href], button:not([disabled]), input'),
      function (el) { return el.offsetParent !== null; }
    );
    if (!live.length) return;
    var first = live[0], last = live[live.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  /* Warm on intent rather than on load. A visitor who never opens the menu
     never pays for it; one who is reaching for it has the markup by the time
     the click lands. touchstart fires roughly 100ms before click on a phone,
     and pointerenter a good deal earlier than that on a pointer device. */
  ['pointerenter', 'touchstart', 'focusin'].forEach(function (evt) {
    document.addEventListener(evt, function (e) {
      var t = e.target;
      if (!(t instanceof Element)) return;
      if (t.closest('[data-sd-open], .menu-drawer-button, [aria-controls="MenuDrawer"]')) warm();
    }, { capture: true, passive: true });
  });

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', function () { drawer = null; INDEX = null; init(); });
})();
